import { registerOverlay } from 'klinecharts'
import { isNum, resolvePricePrecision, type OverlayFigure } from './overlayUtils'
import { type OverlayTemplateParam } from './registrationUtils'

/**
 * Position (risk/reward) tool.
 *
 * Placed with a SINGLE click (see ChartManager.createPositionOverlay, which
 * expands that one click into the four anchors below). Once placed it exposes
 * four draggable grab handles:
 *
 *   points[0] = entry-left   ┐ share the entry price; drag horizontally to set
 *   points[1] = entry-right  ┘ the box width, vertically to move the entry.
 *   points[2] = target       — the "long" handle (take-profit price).
 *   points[3] = stop         — the "short" handle (stop-loss price).
 *
 * Direction is read off the geometry: target above entry ⇒ LONG, below ⇒ SHORT.
 * The profit zone (entry↔target) is shaded green, the loss zone (entry↔stop)
 * red, with a dashed entry line between them. The fill zones and entry line are
 * event-enabled so pressing the body drags the whole position. Labels render as
 * solid pills — green for the profit side, red for the loss side — with white
 * text for legibility.
 */

const PROFIT_FILL = 'rgba(8, 153, 129, 0.16)'
const PROFIT_LINE = '#089981'
const LOSS_FILL = 'rgba(242, 54, 69, 0.16)'
const LOSS_LINE = '#f23645'
// Muted grey, NOT white. The entry sits directly over candles, and a solid
// white rule across the chart is far too much contrast for a divider — it
// competes with the price action it's meant to annotate.
const ENTRY_LINE = 'rgba(120, 124, 134, 0.5)'
const ENTRY_PILL = '#363a45'
const PILL_TEXT = '#ffffff'

/** Price with thousands separators at the chart's own precision. */
function fmtPrice(v: number, precision: number): string {
  return v.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
}

// Grab handles: small dark-filled circles with a blue ring
// that thickens slightly on hover.
const HANDLE_STYLE = {
  color: '#131722',
  borderColor: '#3b82f6',
  borderSize: 1.5,
  radius: 5,
  activeColor: '#131722',
  activeBorderColor: '#3b82f6',
  activeBorderSize: 2.5,
  activeRadius: 6,
}

interface Coord {
  x: number
  y: number
}

/* ------------------------------------------------------------------ */
/*  Cursor feedback                                                     */
/* ------------------------------------------------------------------ */

/**
 * Figure keys. klinecharts hands `figureKey` straight back on mouse events, so
 * tagging figures by ROLE avoids mapping cursors off array positions — that
 * list changes shape between hover states and would drift silently.
 */
export const FIG_BODY = 'pos_body'
/**
 * Anchor-keyed figures. The patched klinecharts treats a figure keyed
 * `anchor_<n>` as the drag handle for points[n] — so the target/stop BARS
 * resize directly and need no dots of their own. See
 * patches/klinecharts@9.8.12.patch ("let a CUSTOM figure act as the drag
 * handle for a specific anchor").
 */
export const FIG_ANCHOR = (index: number) => `anchor_${index}`
export const ANCHOR_ENTRY_LEFT = 0
export const ANCHOR_ENTRY_RIGHT = 1
export const ANCHOR_TARGET = 2
export const ANCHOR_STOP = 3

/* ------------------------------------------------------------------ */
/*  Default geometry for a freshly-placed position                      */
/* ------------------------------------------------------------------ */

/** Risk leg as a fraction of the VISIBLE price span. Box total height is
 *  3x this (risk + 2x reward), i.e. ~24% of the viewport - clearly on-screen
 *  at any zoom without dominating the chart. */
const RISK_VIEWPORT_FRACTION = 0.08
/** Fallback risk when the viewport span is unusable (flat/degenerate data). */
const RISK_PRICE_FRACTION = 0.01
/** Reward:risk. The house default. */
const REWARD_MULTIPLE = 2
/** Box width as a fraction of the visible bar count, then clamped. */
const WIDTH_VIEWPORT_FRACTION = 0.2
const MIN_HALF_WIDTH = 3
const MAX_HALF_WIDTH = 60

export interface PositionGeometryInput {
  entry: number
  visibleLow: number
  visibleHigh: number
  visibleBars: number
  short: boolean
}

interface ExecutedTradeOverlayData {
  kind: 'executedTrade'
  side: 'Long' | 'Short'
  quantity: number
  pnl: number
}

function executedTradeData(value: unknown): ExecutedTradeOverlayData | null {
  const data = value as Partial<ExecutedTradeOverlayData> | undefined
  return data?.kind === 'executedTrade' &&
    (data.side === 'Long' || data.side === 'Short') &&
    isNum(data.quantity) &&
    isNum(data.pnl)
    ? (data as ExecutedTradeOverlayData)
    : null
}

/**
 * Default target/stop/width for a newly placed position tool.
 *
 * Sized from the VISIBLE viewport rather than a fixed percentage of price.
 * A flat `entry * 1.05` target is meaningless across timeframes: on a 5-minute
 * chart spanning 0.5% it lands far off-screen (the box reads as enormous), and
 * on a multi-year chart it's a sliver. Anchoring to what's actually on screen
 * keeps the tool usable at any zoom.
 *
 * Reward is always `REWARD_MULTIPLE` x risk.
 */
export function positionDefaultGeometry({
  entry,
  visibleLow,
  visibleHigh,
  visibleBars,
  short,
}: PositionGeometryInput): { target: number; stop: number; halfWidth: number } {
  const span = visibleHigh - visibleLow
  // Degenerate viewport (flat data, or called before the first layout) -> fall
  // back to a price-relative box so the tool still appears at a sane size.
  const risk =
    isNum(span) && span > 0 ? span * RISK_VIEWPORT_FRACTION : Math.abs(entry) * RISK_PRICE_FRACTION
  const reward = risk * REWARD_MULTIPLE

  const halfWidth = Math.min(
    MAX_HALF_WIDTH,
    Math.max(
      MIN_HALF_WIDTH,
      Math.round(
        ((isNum(visibleBars) && visibleBars > 0 ? visibleBars : 40) * WIDTH_VIEWPORT_FRACTION) / 2,
      ),
    ),
  )

  return {
    target: short ? entry - reward : entry + reward,
    stop: short ? entry + risk : entry - risk,
    halfWidth,
  }
}

type Figure = OverlayFigure

function rect(
  x: number,
  yTop: number,
  width: number,
  height: number,
  color: string,
  draggable = false,
): Figure {
  return {
    type: 'rect',
    ignoreEvent: !draggable,
    attrs: { x, y: yTop, width, height },
    styles: { style: 'fill', color },
  }
}

function hline(
  x1: number,
  x2: number,
  y: number,
  color: string,
  dashed = false,
  draggable = false,
): Figure {
  return {
    type: 'line',
    ignoreEvent: !draggable,
    attrs: {
      coordinates: [
        { x: x1, y },
        { x: x2, y },
      ],
    },
    styles: dashed ? { color, style: 'dashed', dashedValue: [4, 4] } : { color },
  }
}

/** Solid rounded pill with white text — used for the price / R:R readouts. */
function pill(
  x: number,
  y: number,
  text: string,
  bg: string,
  align: 'left' | 'right' | 'center' = 'left',
  borderColor?: string,
): Figure {
  return {
    type: 'text',
    ignoreEvent: true,
    attrs: { x, y, text, baseline: 'middle', align },
    styles: {
      style: 'fill',
      color: PILL_TEXT,
      size: 11,
      backgroundColor: bg,
      paddingLeft: 5,
      paddingRight: 5,
      paddingTop: 3,
      paddingBottom: 3,
      borderRadius: 3,
      ...(borderColor ? { borderColor, borderSize: 1 } : {}),
    },
  }
}

export function buildFigures(
  coordinates: Coord[],
  points: Array<{ value?: number }>,
  pricePrecision: number,
  selected = true,
  extendData?: unknown,
): Figure[] {
  const entryL = coordinates[0]
  const entryR = coordinates[1]
  const targetC = coordinates[2]
  const stopC = coordinates[3]
  // Nothing to draw until the single-click placement has expanded to the full
  // four-anchor box (entry-left + entry-right at minimum).
  if (!entryL || !entryR) return []

  const leftX = Math.min(entryL.x, entryR.x)
  const rightX = Math.max(entryL.x, entryR.x)
  const width = Math.max(rightX - leftX, 1)
  const entryY = entryL.y
  const trade = executedTradeData(extendData)
  const tradeColor = trade ? (trade.pnl >= 0 ? PROFIT_LINE : LOSS_LINE) : null
  const tradeFill = trade ? (trade.pnl >= 0 ? PROFIT_FILL : LOSS_FILL) : null

  const figures: Figure[] = []

  // Profit zone (entry ↔ target) — fill only, no top border. Draggable so the
  // body moves.
  if (targetC) {
    const top = Math.min(entryY, targetC.y)
    const h = Math.abs(targetC.y - entryY)
    if (h > 0)
      figures.push({
        ...rect(leftX, top, width, h, tradeFill ?? PROFIT_FILL, true),
        key: FIG_BODY,
      })
  }

  // Loss zone (entry ↔ stop) — fill only, no bottom border.
  if (stopC && !trade) {
    const top = Math.min(entryY, stopC.y)
    const h = Math.abs(stopC.y - entryY)
    if (h > 0) figures.push({ ...rect(leftX, top, width, h, LOSS_FILL, true), key: FIG_BODY })
  }

  // Entry line — muted grey, draggable (grabbing it moves the whole position).
  // The only line present at rest; the coloured borders are hover chrome.
  figures.push({ ...hline(leftX, rightX, entryY, ENTRY_LINE, false, true), key: FIG_BODY })

  // Everything below is HOVER/SELECT-only chrome. `selected` is fed by
  // `registerLabelVisibilityResolver`, which reports hover OR click — so at
  // rest the tool is just two soft zones and one muted entry line, and the
  // borders, readouts and grab dots all arrive together on hover.
  if (!selected) return figures

  // Target/stop borders — solid, in the zone's own colour, on the outer edge of
  // each zone. These are the drag targets for target/stop (no dot handles; the
  // cursor changes instead), so they are draggable.
  // Keyed to their anchors, so grabbing the BAR drags that price directly —
  // no dot handles needed on target/stop.
  if (targetC)
    figures.push({
      ...hline(leftX, rightX, targetC.y, tradeColor ?? PROFIT_LINE, false, true),
      key: FIG_ANCHOR(ANCHOR_TARGET),
    })
  if (stopC && !trade)
    figures.push({
      ...hline(leftX, rightX, stopC.y, LOSS_LINE, false, true),
      key: FIG_ANCHOR(ANCHOR_STOP),
    })

  // Width dots on the entry ends — the one affordance with no other signal.
  // Also anchor-keyed, so they resize rather than translating the box.
  for (const [x, anchor] of [
    [leftX, ANCHOR_ENTRY_LEFT],
    [rightX, ANCHOR_ENTRY_RIGHT],
  ] as const) {
    figures.push({
      key: FIG_ANCHOR(anchor),
      type: 'circle',
      ignoreEvent: false,
      attrs: { x, y: entryY, r: 4 },
      styles: {
        style: 'stroke_fill',
        color: HANDLE_STYLE.color,
        borderColor: HANDLE_STYLE.borderColor,
        borderSize: HANDLE_STYLE.borderSize,
      },
    })
  }

  // NOTE: the draggable grab handles are klinecharts' DEFAULT point figures
  // (see `needDefaultPointFigure` on the template), NOT figures returned here.
  // Only default point figures resize; anything emitted from this callback
  // translates the whole overlay instead. Drawing our own dots here produced
  // handles that looked right and did the wrong thing.

  const entry = points[0]?.value
  const target = points[2]?.value
  const stop = points[3]?.value
  const midX = (leftX + rightX) / 2

  // Price + signed-percent label, e.g. "Target $66,126.55 (+1.53%)".
  const priceLabel = (kind: string, price: number) => {
    const pct = entry !== undefined && entry !== 0 ? ((price - entry) / entry) * 100 : 0
    const sign = pct >= 0 ? '+' : '\u2212'
    return `${kind} $${fmtPrice(price, pricePrecision)} (${sign}${Math.abs(pct).toFixed(2)}%)`
  }

  // Target pill (green) — centred ON the target line, matching the reference.
  if (isNum(entry) && isNum(target) && targetC) {
    const targetLabel = trade
      ? (() => {
          const rawPct = entry === 0 ? 0 : ((target - entry) / entry) * 100
          const pct = rawPct * (trade.side === 'Short' ? -1 : 1)
          const sign = pct >= 0 ? '+' : '\u2212'
          return `Exit $${fmtPrice(target, pricePrecision)} (${sign}${Math.abs(pct).toFixed(2)}%)`
        })()
      : priceLabel('Target', target)
    figures.push(pill(midX, targetC.y, targetLabel, tradeColor ?? PROFIT_LINE, 'center'))
  }

  // Stop pill (red) — centred ON the stop line.
  if (!trade && isNum(entry) && isNum(stop) && stopC) {
    figures.push(pill(midX, stopC.y, priceLabel('Stop', stop), LOSS_LINE, 'center'))
  }

  // Entry pill (slate) — direction, entry price and the live R/R, on the entry
  // line. Replaces the old standalone "Risk/reward ratio" pill: one label on
  // the line the user is actually dragging reads far better than two.
  if (isNum(entry)) {
    const quantity = trade?.quantity.toLocaleString('en-US', { maximumFractionDigits: 4 })
    const pnl = trade
      ? `${trade.pnl >= 0 ? '+' : '\u2212'}$${Math.abs(trade.pnl).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : null
    const parts = trade
      ? [`${trade.side} ${quantity} @ $${fmtPrice(entry, pricePrecision)}`, `Gross P&L ${pnl}`]
      : [
          `${isNum(target) && target < entry ? 'Short' : 'Long'} $${fmtPrice(entry, pricePrecision)}`,
        ]
    if (!trade && isNum(target) && isNum(stop)) {
      const risk = Math.abs(entry - stop)
      if (risk > 0) parts.push(`R/R ${(Math.abs(target - entry) / risk).toFixed(2)}`)
    }
    figures.push(pill(midX, entryY, parts.join(' \u00b7 '), ENTRY_PILL, 'center'))
  }

  return figures
}

/** Colored price pills on the y-axis for entry / target / stop. */
export function buildYAxisFigures(
  coordinates: Coord[],
  points: Array<{ value?: number }>,
  axisWidth: number,
  pricePrecision: number,
  selected = true,
  extendData?: unknown,
): Figure[] {
  const entryC = coordinates[0]
  const targetC = coordinates[2]
  const stopC = coordinates[3]
  // Axis price pills are labels too — hide them unless the box is selected.
  if (!entryC || !selected) return []

  const figures: Figure[] = []
  const axisPill = (y: number, value: number, bg: string): Figure =>
    pill(axisWidth, y, value.toFixed(pricePrecision), bg, 'right')

  const entry = points[0]?.value
  const target = points[2]?.value
  const stop = points[3]?.value
  const trade = executedTradeData(extendData)
  const exitColor = trade?.pnl != null && trade.pnl < 0 ? LOSS_LINE : PROFIT_LINE

  if (isNum(entry)) figures.push(axisPill(entryC.y, entry, ENTRY_PILL))
  if (isNum(target) && targetC) figures.push(axisPill(targetC.y, target, exitColor))
  if (!trade && isNum(stop) && stopC) figures.push(axisPill(stopC.y, stop, LOSS_LINE))
  return figures
}

/**
 * Labels (target/stop/R:R pills + axis prices) should appear exactly when the
 * default grab handles do — i.e. while the box is hovered OR selected. klinecharts
 * gates the handles on its live hover/click instance info, but does NOT pass that
 * into createPointFigures. So each ChartManager registers a resolver here, bound to
 * its overlay store, that answers "is this overlay currently hovered/clicked?".
 * On every redraw the render callbacks consult it, keeping labels in lockstep with
 * the handles without any extendData round-trip.
 */
type LabelVisibilityResolver = (overlayId: string) => boolean
const labelResolvers = new Set<LabelVisibilityResolver>()

export function registerLabelVisibilityResolver(fn: LabelVisibilityResolver): () => void {
  labelResolvers.add(fn)
  return () => labelResolvers.delete(fn)
}

function isPositionActive(overlayId: string): boolean {
  for (const fn of labelResolvers) if (fn(overlayId)) return true
  return false
}

/** Long and Short are independent drawings sharing one template; only their
 * default anchor geometry differs (set in ChartManager.expandPositionOverlay). */
export const POSITION_TOOL_NAMES = ['longPositionTool', 'shortPositionTool'] as const

export function registerRiskRewardOverlay(): void {
  for (const name of POSITION_TOOL_NAMES) {
    registerOverlay({
      name,
      totalStep: 2, // single click to place; ChartManager expands it to 4 anchors
      // MUST stay true. klinecharts only routes a drag to `eventPressedPointMove`
      // (i.e. RESIZE) when the pressed figure is a DEFAULT point figure —
      // `figureType === EventOverlayInfoFigureType.Point`, dist/index.esm.js:8519.
      // Figures returned from `createPointFigures` are "other" and fall through
      // to `eventPressedOtherMove`, which translates the whole overlay. Turning
      // this off is what broke resizing on the target/stop bars and the width
      // handles.
      // Every drag handle is now an anchor-keyed CUSTOM figure (the bars
      // themselves, plus the two entry width dots), so klinecharts' own dots
      // would be redundant clutter on target/stop.
      needDefaultPointFigure: false,
      needDefaultXAxisFigure: true,
      needDefaultYAxisFigure: false,
      styles: { point: HANDLE_STYLE },
      performEventPressedMove: ({ points, performPointIndex }) => {
        // Dragging either entry handle keeps the entry line flat: sync both prices.
        if (performPointIndex === 0 || performPointIndex === 1) {
          const v = points[performPointIndex]?.value
          if (typeof v === 'number') {
            if (points[0]) points[0].value = v
            if (points[1]) points[1].value = v
          }
        }
        // Target/stop handles stay glued to the box's left edge, so resizing the
        // box (dragging an entry handle) drags them along instead of stranding them.
        const p0 = points[0]
        const p1 = points[1]
        if (p0 && p1) {
          const left = (p0.dataIndex ?? 0) <= (p1.dataIndex ?? 0) ? p0 : p1
          for (const i of [2, 3] as const) {
            const pt = points[i]
            if (pt) {
              pt.dataIndex = left.dataIndex
              pt.timestamp = left.timestamp
            }
          }
        }
      },
      createPointFigures: ({ coordinates, overlay, precision, yAxis }) => {
        if (coordinates.length <= 1) return []
        const p = resolvePricePrecision(yAxis, precision)
        return buildFigures(
          coordinates as Coord[],
          overlay.points as Array<{ value?: number }>,
          p,
          isPositionActive(overlay.id),
          overlay.extendData,
        )
      },
      createYAxisFigures: ({ coordinates, overlay, bounding, precision, yAxis }) => {
        if (coordinates.length <= 1) return []
        const p = resolvePricePrecision(yAxis, precision)
        return buildYAxisFigures(
          coordinates as Coord[],
          overlay.points as Array<{ value?: number }>,
          bounding.width,
          p,
          isPositionActive(overlay.id),
          overlay.extendData,
        )
      },
    } as OverlayTemplateParam)
  }
}
